import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Star, ArrowLeft, Shield, Award, Zap, Lock, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { key: "prompt_quality",   label: "Quality of Prompt" },
  { key: "business_value",   label: "Practical Business Value" },
  { key: "clarity",          label: "Clarity & Ease of Use" },
  { key: "depth",            label: "Depth of Expertise" },
  { key: "effectiveness",    label: "Overall Effectiveness" },
];

const FUNCTION_LABELS = {
  procurement: "Procurement", finance: "Finance", marketing: "Marketing",
  hr: "HR", sales: "Sales", operations: "Operations", general: "General",
};

const StarDisplay = ({ rating, size = 13 }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={size}
        className={i <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}
      />
    ))}
  </div>
);

const StarSelector = ({ value, onChange, disabled }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <button key={i} type="button" disabled={disabled}
          onClick={() => onChange(i === value ? 0 : i)}
          onMouseEnter={() => !disabled && setHover(i)}
          onMouseLeave={() => setHover(0)}
          className={`p-0.5 transition-transform ${!disabled ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
        >
          <Star size={22} className={(hover || value) >= i ? "text-amber-400 fill-amber-400" : "text-slate-300 fill-slate-300"} />
        </button>
      ))}
    </div>
  );
};

const BadgeChip = ({ type }) => {
  const styles = {
    official_content: { cls: "bg-blue-100 text-blue-700 border border-blue-200", icon: Shield, label: "Official Platform Content" },
    top_contributor:  { cls: "bg-violet-100 text-violet-700 border border-violet-200", icon: Award, label: "Top Contributor" },
    highly_rated:     { cls: "bg-amber-100 text-amber-700 border border-amber-200", icon: Star, label: "Highly Rated" },
  };
  const s = styles[type]; if (!s) return null;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
};

export default function ContributorProfilePage() {
  const { contributorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [contributor, setContributor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Rating state
  const [myRating, setMyRating] = useState({});
  const [ratingScores, setRatingScores] = useState({});
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [contribRes] = await Promise.all([
          axios.get(`${API}/contributors/${contributorId}`),
        ]);
        setContributor(contribRes.data);

        if (user) {
          try {
            const ratingRes = await axios.get(`${API}/contributors/${contributorId}/my-rating`, { withCredentials: true });
            if (ratingRes.data.scores) {
              setMyRating(ratingRes.data);
              setRatingScores(ratingRes.data.scores);
            }
          } catch {}
        }
      } catch {
        setError("Contributor not found");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [contributorId, user]);

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    const hasAnyScore = Object.values(ratingScores).some(s => s > 0);
    if (!hasAnyScore) { toast.error("Please rate at least one category."); return; }
    setRatingSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/contributors/${contributorId}/rate`,
        { scores: ratingScores },
        { withCredentials: true }
      );
      setContributor(prev => ({ ...prev, ...res.data }));
      setMyRating({ scores: ratingScores });
      setShowRatingForm(false);
      toast.success(myRating.scores ? "Rating updated!" : "Rating submitted!", { description: "Thanks for your feedback." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit rating.");
    } finally {
      setRatingSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !contributor) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">{error || "Not found"}</p>
          <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
        </div>
      </div>
    );
  }

  const isPromptlee = contributor.is_platform_author;
  const apps = contributor.applications || [];

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors">
          <ArrowLeft size={15} /> Back
        </button>

        <div className="grid lg:grid-cols-3 gap-6">

          {/* ── Left sidebar ── */}
          <div className="lg:col-span-1 space-y-5">

            {/* Profile card */}
            <div className={`rounded-2xl border p-6 ${isPromptlee ? "bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
              {/* Avatar */}
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold mb-4 ${isPromptlee ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
                {isPromptlee ? "W" : (contributor.name?.[0] || "C").toUpperCase()}
              </div>
              <h1 className={`text-xl font-bold mb-1 ${isPromptlee ? "text-white" : "text-slate-900"}`}>{contributor.name}</h1>
              {contributor.title && (
                <p className={`text-sm mb-3 ${isPromptlee ? "text-slate-300" : "text-slate-500"}`}>{contributor.title}</p>
              )}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(contributor.badges || []).map(b => <BadgeChip key={b} type={b} />)}
              </div>
              {contributor.bio && (
                <p className={`text-sm leading-relaxed ${isPromptlee ? "text-slate-300" : "text-slate-600"}`}>{contributor.bio}</p>
              )}
            </div>

            {/* Stats */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{contributor.contribution_count}</p>
                  <p className="text-xs text-slate-500">Applications</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">
                    {contributor.overall_rating > 0 ? contributor.overall_rating : "—"}
                  </p>
                  <p className="text-xs text-slate-500">Avg Rating</p>
                </div>
              </div>
              {contributor.overall_rating > 0 && (
                <div className="flex justify-center mt-3">
                  <StarDisplay rating={contributor.overall_rating} size={14} />
                </div>
              )}
              {contributor.rating_count > 0 && (
                <p className="text-xs text-slate-400 text-center mt-1">{contributor.rating_count} ratings</p>
              )}
            </div>

            {/* Expertise areas */}
            {contributor.expertise_areas?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Areas of Expertise</h3>
                <div className="flex flex-wrap gap-1.5">
                  {contributor.expertise_areas.map(a => (
                    <span key={a} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 rounded-full font-medium capitalize">
                      {FUNCTION_LABELS[a] || a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Category ratings */}
            {Object.keys(contributor.ratings || {}).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Category Ratings</h3>
                <div className="space-y-3">
                  {CATEGORIES.map(cat => {
                    const r = contributor.ratings?.[cat.key];
                    if (!r) return null;
                    return (
                      <div key={cat.key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-600">{cat.label}</span>
                          <span className="text-xs font-semibold text-slate-800">{r.avg}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(r.avg / 5) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Main content ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Rate this contributor */}
            {user && (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="rate-contributor-section">
                <button
                  onClick={() => setShowRatingForm(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  data-testid="toggle-rating-form-btn"
                >
                  <div className="flex items-center gap-2">
                    <Star size={15} className="text-amber-500 fill-amber-500" />
                    <span className="font-semibold text-slate-800 text-sm">
                      {myRating.scores ? "Update your rating" : `Rate ${isPromptlee ? "Promptlee content" : contributor.name}`}
                    </span>
                    {myRating.scores && <Badge className="text-xs bg-emerald-50 text-emerald-700 border-0">Rated</Badge>}
                  </div>
                  {showRatingForm ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
                </button>
                {showRatingForm && (
                  <div className="px-5 pb-5 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mt-4 mb-4">Rate across 5 expertise dimensions. All categories are optional.</p>
                    <form onSubmit={handleSubmitRating} className="space-y-3">
                      {CATEGORIES.map(cat => (
                        <div key={cat.key} className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">{cat.label}</span>
                          <StarSelector
                            value={ratingScores[cat.key] || 0}
                            onChange={v => setRatingScores(prev => ({ ...prev, [cat.key]: v }))}
                          />
                        </div>
                      ))}
                      <div className="flex justify-end pt-2">
                        <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={ratingSubmitting} data-testid="submit-contributor-rating-btn">
                          {ratingSubmitting ? "Submitting..." : myRating.scores ? "Update Rating" : "Submit Rating"}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {!user && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
                <p className="text-sm text-slate-500">
                  <Link to="/auth" className="text-blue-600 font-medium hover:underline">Sign in</Link> to rate this contributor
                </p>
              </div>
            )}

            {/* Applications */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">
                  {isPromptlee ? "Platform Applications" : "Contributed Applications"}
                  <span className="ml-2 text-sm font-normal text-slate-400">({apps.length})</span>
                </h3>
              </div>
              {apps.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-400">No applications yet.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {apps.map(app => (
                    <Link
                      key={app.app_id}
                      to={`/app/${app.app_id}`}
                      className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors group"
                      data-testid={`contrib-app-${app.app_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 group-hover:text-blue-600 transition-colors text-sm">{app.title}</span>
                          {app.is_premium && <span className="text-xs bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full">Premium</span>}
                          <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{FUNCTION_LABELS[app.function_id] || app.function_id}</span>
                        </div>
                        {app.business_context && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-1">{app.business_context}</p>
                        )}
                      </div>
                      {app.avg_rating > 0 && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Star size={11} className="text-amber-400 fill-amber-400" />
                          <span className="text-xs font-semibold text-slate-600">{app.avg_rating}</span>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
